// SDB SQL row → domain type adapters. Backend returns positional
// column arrays; these helpers turn them into the types under `./types`.

import type { Agent, AgentStatus, RepoConnection, Workspace } from "./types";

export function asNumber(value: unknown): number {
  return typeof value === "number" ? value : Number(value);
}

export function bigintToNumber(value: bigint | number): number {
  return typeof value === "bigint" ? Number(value) : value;
}

export function rowToWorkspace(row: unknown[]): Workspace | null {
  const workspace_id = asNumber(row[0]);
  const org_id = asNumber(row[1]);
  const slug = typeof row[2] === "string" ? row[2] : "";
  const name = typeof row[3] === "string" ? row[3] : "";
  // Columns 4,5 = created_at, created_by (not surfaced here).
  const daytona_sandbox_id = typeof row[6] === "string" ? row[6] : "";
  const daytona_state = typeof row[7] === "string" ? row[7] : "cold";
  const daytona_last_heartbeat = asNumber(row[8] ?? 0);
  if (!workspace_id || !slug || !name) return null;
  return {
    workspace_id,
    org_id,
    slug,
    name,
    daytona_sandbox_id,
    daytona_state,
    daytona_last_heartbeat,
  };
}

export function rowToAgent(row: unknown[]): Agent | null {
  // Positional shape from merge_workspace_agents in the backend:
  //   [agent_id, workspace_id, seat_key, display_name, role_title,
  //    score_state, created_at, status, model]
  const agent_id = asNumber(row[0]);
  const workspace_id = asNumber(row[1]);
  const seat_key = typeof row[2] === "string" ? row[2] : "";
  const display_name = typeof row[3] === "string" ? row[3] : "";
  const role_title = typeof row[4] === "string" ? row[4] : "";
  const score_state = typeof row[5] === "string" ? row[5] : "new";
  const current_model = typeof row[8] === "string" ? row[8] : "unassigned";
  if (!agent_id || !workspace_id || !seat_key) return null;
  return {
    agent_id,
    workspace_id,
    seat_key,
    display_name,
    role_title,
    score_state,
    current_model,
  };
}

export function rowToRepoConnection(row: unknown[]): RepoConnection | null {
  const repo_connection_id = asNumber(row[0]);
  const workspace_id = asNumber(row[1]);
  const github_owner = typeof row[2] === "string" ? row[2] : "";
  const github_repo = typeof row[3] === "string" ? row[3] : "";
  const default_branch = typeof row[4] === "string" ? row[4] : "";
  const connected_at = asNumber(row[5] ?? 0);
  if (!repo_connection_id || !workspace_id || !github_owner || !github_repo) return null;
  return {
    repo_connection_id,
    workspace_id,
    github_owner,
    github_repo,
    default_branch,
    connected_at,
  };
}

export function normaliseAgentStatus(status: string): AgentStatus {
  if (status === "thinking" || status === "replying" || status === "offline") {
    return status;
  }
  return "idle";
}

export function mentionOptions(body: string, agents: Agent[]): Agent[] {
  const lower = body.toLowerCase();
  return agents.filter((agent) => lower.includes(`@${agent.seat_key}`));
}
